import React, { useContext, useEffect, useRef, useState } from "react";
import ModalLayout from "../ModalLayout";
import {
  Container,
  Content,
  SearchOptionsContainer,
  TabButton,
  WithoutStoreContainer,
} from "./styles";
import LoaderAsync from "../LoaderAsync";
import api from "../../services/api";
import StoreCard from "../StoreCard";
import ResponsibleWithdrawalForm from "../ResponsibleWithdrawalForm";
import { DeliveryType } from "../../util/deliveryType";
import StoreSelectorByAddress from "../StoreSelectorByAddress";
import { MdWarning } from "react-icons/md";
import { useSelector } from "react-redux";
import { AlertContext } from "../../contexts/AlertContext";

export default function PickupModal({
  customerAddressId,
  cart,
  store,
  closeModal,
  showOverlay,
  onChoosePickupStore,
}) {
  const searchOptions = {
    close: "C",
    neighborhood: "N",
  };

  const steps = {
    selectStore: 1,
    responsibleWithdrawal: 2,
  };

  const [step, setStep] = useState(steps.selectStore);
  const [loading, setLoading] = useState(false);
  const [searchOptionFocused, setSearchOptionFocused] = useState(
    searchOptions.close
  );
  const [
    allInformationsAreFilledInNeighborhoodSearchOption,
    setAllInformationsAreFilledInNeighborhoodSearchOption,
  ] = useState(false);
  const [stores, setStores] = useState([]);
  const user = useSelector((state) => state.user.profile);
  const { data: storeByUser } = useSelector((state) => state.storeByUser);
  const targetStore = storeByUser || store;
  const { showAlert } = useContext(AlertContext);
  const [selectedStore, setSelectedStore] = useState(null);
  const [responsibleWithdrawal, setResponsibleWithdrawal] = useState(null);

  const formRef = useRef();

  useEffect(() => {
    clearScreenWhenSearchOptionIsChanged();
  }, [searchOptionFocused]);

  useEffect(() => {
    if (customerAddressId) {
      if (searchOptionFocused === searchOptions.close) {
        getClosestStores(customerAddressId, cart);
      }
    }
  }, [searchOptionFocused, customerAddressId, cart, targetStore]);

  function clearScreenWhenSearchOptionIsChanged() {
    setSelectedStore(null);
    setStores([]);
  }

  async function getClosestStores(customerAddressId, cart) {
    try {
      setStores([]);
      setSelectedStore(null);
      setLoading(true);
      showOverlay(true);
      const products = getProductsInCart(cart.data.itens);
      const { data } = await api.post(
        `/stores/customerAddress/${customerAddressId}/pickup`,
        { products: products, storeIdPrimary: targetStore?.id }
      );
      if (data) {
        setStores(data);
        selectDefaultStore(data);
      }
    } catch (error) {
      showAlert({
        message: error.response?.data?.user || "Erro ao obter opções de retirada",
        hideCloseButton: true,
      });
    } finally {
      setLoading(false);
      showOverlay(false);
    }
  }

  function getProductsInCart(updatedCart) {
    if (updatedCart) {
      return updatedCart.map((item) => {
        return {
          productId: item.id,
          amount: item.quantidade,
        };
      });
    } else {
      return cart.itens.map((item) => {
        return {
          productId: item.id,
          amount: item.quantidade,
        };
      });
    }
  }

  function onSelectStore(storeId) {
    const selectedInfoStore = stores.find(
      (infoStore) => String(infoStore.id) === String(storeId)
    );
    setSelectedStore(formatPickupStore(selectedInfoStore));
  }

  function isSelectedStore(infoStore) {
    return Boolean(
      selectedStore?.id &&
      infoStore?.id &&
      String(selectedStore.id) === String(infoStore.id)
    );
  }

  function selectDefaultStore(storeList) {
    if (!Array.isArray(storeList) || !targetStore) {
      setSelectedStore(null);
      return;
    }

    const selectedInfoStore = storeList.find((infoStore) =>
      isSameStore(infoStore, targetStore)
    );

    setSelectedStore(formatPickupStore(selectedInfoStore));
  }

  function formatPickupStore(infoStore) {
    if (!infoStore) return null;

    return {
      ...infoStore,
      tipoEntrega: infoStore.tipoEntrega || DeliveryType.RETIRAR_NA_LOJA,
    };
  }

  function isSameStore(infoStore, targetStore) {
    if (!infoStore || !targetStore) return false;

    if (infoStore.id && targetStore.id && Number(infoStore.id) === Number(targetStore.id)) {
      return true;
    }

    if (infoStore.uuid && targetStore.uuid && infoStore.uuid === targetStore.uuid) {
      return true;
    }

    const infoStoreCnpj = normalizeCnpj(infoStore.cnpj);
    const targetStoreCnpj = normalizeCnpj(targetStore.cnpj);

    return Boolean(infoStoreCnpj && targetStoreCnpj && infoStoreCnpj === targetStoreCnpj);
  }

  function normalizeCnpj(cnpj) {
    if (!cnpj) return "";

    return String(cnpj).replace(/[^0-9]/g, "");
  }

  function showSelectStoreModal() {
    setResponsibleWithdrawal(null);
    setStep(steps.selectStore);
  }

  function onSubmitResponsibleWithdrawalForm(values) {
    var cpfWithoutMask = values.cpf?.replace(/[^\d]+/g, "");
    if (values.retirada_terceiro === "true" && cpfWithoutMask === user.cpf) {
      notifyCPFIsSameOwnerAccount();
      return;
    }

    onChoosePickupStore(selectedStore, values);
    closeModal();
  }

  function notifyCPFIsSameOwnerAccount() {
    showAlert({
      message:
        "O CPF informado é o mesmo do próprietário da conta. Por favor, digite outro CPF ou informe que não será retirado por um terceiro.",
      hideCloseButton: true,
    });
  }

  async function getStoresByAddress(state, city, neighborhood) {
    try {
      setLoading(true);
      showOverlay(true);
      clearScreenWhenSearchOptionIsChanged();
      const products = getProductsInCart(cart.data.itens);
      const { data } = await api.post(
        `/stores/pickup/${state}/${city}/${neighborhood}`,
        { products: products, storeIdPrimary: targetStore?.id }
      );
      if (data) {
        setStores(data);
        selectDefaultStore(data);
      }
    } catch (error) {
      showAlert({
        message: error.response?.data?.user || "Erro ao obter opções de retirada",
        hideCloseButton: true,
      });
      throw error;
    } finally {
      setLoading(false);
      showOverlay(false);
    }
  }

  async function onChangeStoreAddress(state, city, neighborhood) {
    if (state && city && neighborhood) {
      setAllInformationsAreFilledInNeighborhoodSearchOption(true);
      await getStoresByAddress(state, city, neighborhood);
    } else {
      setAllInformationsAreFilledInNeighborhoodSearchOption(false);
    }
  }

  return (
    <>
      {step === steps.responsibleWithdrawal && (
        <ModalLayout
          showModal={true}
          handleCloseModal={showSelectStoreModal}
          title="Quem vai retirar os produtos?"
          buttonLeft={{
            text: "Avançar",
            action: () => {
              if (formRef.current) formRef.current.submitForm();
            },
            disabled: !selectedStore,
          }}
          buttonRight={{
            text: "Voltar",
            action: showSelectStoreModal,
          }}
        >
          <Container>
            <ResponsibleWithdrawalForm
              formRef={formRef}
              responsibleWithdrawal={{
                withdrawalByThird: responsibleWithdrawal?.retirada_terceiro,
                fullName: responsibleWithdrawal?.nome_completo,
                cpf: responsibleWithdrawal?.cpf,
                phone: responsibleWithdrawal?.telefone_contato,
              }}
              onSubmitResponsibleWithdrawalForm={
                onSubmitResponsibleWithdrawalForm
              }
            />
          </Container>
        </ModalLayout>
      )}

      <div style={{ display: step === steps.selectStore ? "block" : "none" }}>
        <ModalLayout
          showModal={true}
          handleCloseModal={closeModal}
          title="Retire na farmácia"
          buttonLeft={{
            text: "Confirmar",
            action: () => {
              setStep(steps.responsibleWithdrawal);
            },
            disabled: !selectedStore,
          }}
          buttonRight={{
            text: "Voltar",
            action: closeModal,
          }}
        >
          <SearchOptionsContainer>
            <TabButton
              focused={searchOptionFocused === searchOptions.close}
              onClick={() => setSearchOptionFocused(searchOptions.close)}
            >
              Farmácias próximas
            </TabButton>
            <TabButton
              focused={searchOptionFocused === searchOptions.neighborhood}
              onClick={() => setSearchOptionFocused(searchOptions.neighborhood)}
            >
              Busca por bairro
            </TabButton>
          </SearchOptionsContainer>
          <Content>
            {searchOptionFocused === searchOptions.neighborhood && (
              <StoreSelectorByAddress
                showOverlay={showOverlay}
                onChangeStoreAddress={onChangeStoreAddress}
              />
            )}
            {loading ? (
              <LoaderAsync
                message={
                  searchOptionFocused === searchOptions.close
                    ? "Buscando lojas próximas ao seu endereço..."
                    : "Buscando lojas..."
                }
              />
            ) : stores && stores.length > 0 ? (
              stores.map((infoStore) => (
                <StoreCard
                  key={infoStore.id}
                  name="pickup-modal"
                  store={{
                    storeId: infoStore.id,
                    storeName: infoStore.nome_fantasia,
                    address: {
                      street: infoStore.endereco,
                      number: infoStore.endereco_numero,
                      neighborhood: infoStore.bairro,
                      city: infoStore.cidade,
                      uf: infoStore.uf || infoStore.estado,
                    },
                    distance: infoStore.distancia,
                    operationTime: infoStore.lojaHorario,
                    cnpj: infoStore.cnpj,
                    pharmacistName: infoStore.farmaceutico_nome,
                    pharmacistRegistration: infoStore.farmaceutico_inscricao,
                    afe: infoStore.caminho_afe,
                  }}
                  selectedStore={isSelectedStore(infoStore)}
                  onSelectStore={onSelectStore}
                />
              ))
            ) : (
              (searchOptionFocused !== searchOptions.neighborhood ||
                (searchOptionFocused === searchOptions.neighborhood &&
                  allInformationsAreFilledInNeighborhoodSearchOption)) && (
                <WithoutStoreContainer>
                  <MdWarning size={24} color="yellow" />
                  <p>Não existem farmácias que atendam no seu endereço.</p>
                </WithoutStoreContainer>
              )
            )}
          </Content>
        </ModalLayout>
      </div>
    </>
  );
}
